import pickBy from 'lodash/pickBy';
import moment from 'moment';
import { logoutRequest } from 'src/api/logout';
import { getOrgPrettyName } from 'src/api/utils';
import { DEMO_ORGANISATIONS, ENVIRONMENT } from 'src/globals';
import { isValidString } from 'src/Library/Helper';
import * as routes from 'src/routing/utils';
import { removeSocket } from 'src/sockets/setup';
import { clearBannerDismissal } from 'src/utils/maintenance';
import * as XLSX from 'xlsx';

declare let window: any;

const numberFormatter = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  minimumFractionDigits: 0,
});

export const logoutUser = async (callLogoutApi?: boolean) => {
  // Call an API to logout the user
  if (callLogoutApi) {
    await logoutRequest();
  }
  // Remove auth storage
  const storage = window.localStorage;
  storage.removeItem('userId');
  storage.removeItem('authToken');
  storage.removeItem('organisationId');
  storage.removeItem('type');
  storage.removeItem('passwordExpired');
  clearBannerDismissal();
  removeSocket();
};

export const timeoutPromise = (interval) => {
  return new Promise((resolve) => {
    setTimeout(resolve, interval);
  });
};

export const antFilterByLabel = (input, option) => {
  return (option.props.children || '').toLowerCase().indexOf((input || '').toLowerCase()) >= 0;
};

export const elementOuterHeight = (el: HTMLElement) => {
  let height = el.offsetHeight;
  const style = getComputedStyle(el);

  height += parseInt(style.marginTop || '0', 10) + parseInt(style.marginBottom || '0', 10);
  return height;
};

export const defaultSorter = (a, b) => {
  if (a < b) {
    return -1;
  }
  if (a === b) {
    return 0;
  }
  return 1;
};

export const handleXLSXFile = async (
  file,
  headers,
  parseData = true,
  fileName = '',
  range = 1
): Promise<any> => {
  // To lazy load xlsx module using dynamic import of webpack
  // const XLSX = await import(
  //   /* webpackChunkName: "xlsx" */
  //   'xlsx'
  // );
  return new Promise((resolve, reject) => {
    // Added New Code to Support XLSX File Starts
    // define reader
    const reader = new FileReader();
    let result = [];
    let fileValue;
    // A handler for the load event (just defining it, not executing it right now)
    reader.onload = function (e) {
      fileValue = reader.result;
      const wb = XLSX.read(fileValue, {
        type: 'binary',
        codepage: 65001,
        dateNF: 'yyyy-mm-dd',
        cellDates: true,
      });
      if (fileName === 'Bids_data') {
        const sheetNames: string[] = wb.SheetNames;
        if (sheetNames.indexOf('_hashed_data_headers') !== -1) {
          const headerHashSheet = wb.Sheets._hashed_data_headers;
          const desiredCell = 'A2';
          const headerHashContent = headerHashSheet[desiredCell];
          const headerHash = headerHashContent.v;
          localStorage.setItem('hh', headerHash);
        }
      }
      const wsname = fileName ? fileName : wb.SheetNames[0];
      const ws = wb.Sheets[wsname];
      /* sheet to json */
      /* array inside array without header */
      result = XLSX.utils.sheet_to_json(wb.Sheets[wsname], {
        range,
        header: headers,
        defval: '',
        raw: parseData,
      });
      resolve(result);
    };
    reader.readAsBinaryString(file);
  });
};

export const handleDownloadDump = async (fileResultArray) => {
  // const XLSX = await import(
  //   /* webpackChunkName: "xlsx" */
  //   'xlsx'
  // );

  /* make the worksheet */
  const ws = XLSX.utils.json_to_sheet(fileResultArray);

  /* add to workbook */
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Download Dump');

  /* generate an XLSX file */
  XLSX.writeFile(wb, `dump_js-${moment().format('DD-MMM-YYYY')}.xlsx`);
};

export const validateEmail = (email) => {
  // tslint:disable-next-line:max-line-length
  const re = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(String(email).toLowerCase());
};

export const validateNumber = (number) => {
  // tslint:disable-next-line:radix
  return number !== null && number !== undefined && !isNaN(number.toString());
};

export const escapeRegExp = (str) => {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); // $& means the whole matched string
};

export const removeArrayDuplicates = (arr) => {
  return arr.filter((item, pos) => arr.indexOf(item) === pos);
};

export const downloadFileData = (data, fileName, extensionIncluded = false) => {
  const url = window.URL.createObjectURL(new Blob([data]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', `${fileName}${extensionIncluded ? '' : '.xlsx'}`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const downloadS3Url = async (url, fileName) => {
  try {
    // Fetch the file content
    const response = await fetch(url);
    const blob = await response.blob();

    // Create a temporary URL for the blob
    const tempUrl = URL.createObjectURL(blob);

    // Create a temporary <a> element to trigger the download
    const link = window.document.createElement('a');  // Correct usage
    link.href = tempUrl;
    link.download = fileName;  // Specify the file name here
    window.document.body.appendChild(link);   // Append the link to the document
    link.click();                      // Programmatically click the link to start the download
    window.document.body.removeChild(link);   // Clean up by removing the link
    URL.revokeObjectURL(tempUrl);      // Clean up the object URL
  } catch (error) {
    console.error('Download failed:', error);
  }
};

export const getPriceString = (number) => {
  return numberFormatter.format(number);
};

export const getFileExtension = (filename) => {
  const ext = /^.+\.([^.]+)$/.exec(filename);
  return ext == null ? '' : ext[1].toLowerCase();
};

export const isUserCHAOrFF = () => {
  const userType = window.localStorage.type;
  return ['CHA', 'FF'].indexOf(userType) > -1;
};

export const getDuration = (elem) => {
  if (!elem) {
    return undefined;
  }
  const refreshAt = moment(elem);
  const duration = moment.duration(moment().diff(refreshAt));
  const durationMinutes = duration.minutes();
  return durationMinutes;
};

export const RouteMapping = (routeParams) => {
  return {
    RFQSHIPPER: routes.getRFQAllPlansRoute(),
    DASHBOARDS: routes.getAllDashboardsRoute(),
    RFQSHIPPINGLINE: routes.getRFQVendorRoute(),
    RFQFF: routes.getRFQVendorRoute(),
    EXCEPTIONRULES: routes.getExceptionRulesRoute(),
    ALERTDEFINITIONS: routes.getAlertDefinitionsRoute(),
    ALERTINSTANCES: routes.getAlertInstancesRoute(),
    CONTAINERTRACKING: routes.getContainerTrackingRoute(''),
    CONTAINERTRACKINGCONTAINERS: routes.getContainerTrackingRoute(null,'container'),// open container page instead of insight page
    CONTAINERTRACKINGSHIPMENT: routes.getContainerTrackingRoute(null, 'shipment'), // open shipment page instead of insight page
    CONTAINERTRACKINGBL: routes.getContainerTrackingRoute(null, 'bl'), // open bl page instead of insight page
    CONTAINERTRACKINGIMPORTS: routes.getContainerTrackingRoute('import'),
    CONTAINERTRACKINGEXPORTS: routes.getContainerTrackingRoute('export'),
    IMPORTS: routes.getImportRoute(),
    EXPORTS: routes.getExportsRoute(),
    SALESORDEREXPORT: routes.getSalesOrderRoute('export'),
    SALESORDERIMPORT: routes.getSalesOrderRoute('import'),
    RAKE: routes.getRakeRoute(),
    INQUIRYFF: routes.getInquiryFFRoute(),
    INQUIRYSHIPPER: routes.getInquiryShipperRoute(),
    USERMANAGEMENT: routes.getUserManagementRoute(),
    PERMISSIONS: routes.getPermissionsRoute(),
    TNC: routes.getTnCRoute(),
    CHARGE_MASTER: routes.getChargeMasterRoute(),
    ACCOUNT: routes.getAccountRoute(),
    ORGANISATION: routes.getOrganisationRoute(),
    RATEMASTER: routes.getRateMasterRoute(),
    MARKUP: routes.getMarkupAutomationToolRoute(),
    SHIPMENTEXECUTIONSHIPPER: routes.getShipmentExecutionShipperRoute(),
    SHIPMENTEXECUTIONFF: routes.getShipmentExecutionFFRoute(),
    DISCOVER_RATES:
      routeParams.redirectToSpotRatesLandingPage
        ? routes.getDiscoverRatesLandingPageRoute()
        : routes.getDiscoverRatesRoute(routeParams.viewType),
    ANALYTICS: routes.getAnalyticsRoute(),
    AIRTRACKING: routes.getFreightForwardedRoute(),
    // HOME: routes.getHomeRoute(),
  };
};

const ISODateStringMatchRegex = /[0-9]{4}-[0-9]{2}-[0-9]{2}T[0-9]{2}:[0-9]{2}:[0-9]{2}\.[0-9]+Z/g;

export const replaceFormattedDates = (string, format) =>
  string.replace(ISODateStringMatchRegex, (match) => moment(match).format(format));

export const isNotProduction = () => {
  return !ENVIRONMENT || ENVIRONMENT === 'dev' || ENVIRONMENT === 'demo';
};

export const isDev = () => {
  return !ENVIRONMENT || ENVIRONMENT === 'dev';
};

export const snakeToCamel = (str) => {
  return str.replace(/([-_][a-z])/gi, ($1) => {
    return $1.toUpperCase().replace('-', '').replace('_', '');
  });
};

export const convertToCamelCaseObject = (data: any) => {
  let newData: any;
  let origKey: string;
  let newKey: string;
  let value: any;
  if (data instanceof Array) {
    return data.map((item) => {
      if (typeof item === 'object') {
        return convertToCamelCaseObject(item);
      }
      return item;
    });
  }
  newData = {};
  for (origKey in data) {
    if (data.hasOwnProperty(origKey)) {
      newKey = snakeToCamel(origKey);
      value = data[origKey];
      if (value instanceof Array || typeof value === 'object') {
        value = convertToCamelCaseObject(value);
      }
      newData[newKey] = value;
    }
  }
  return newData;
};

export const getRegexWithEscapedSpecialCharacters = (str, option) => {
  return new RegExp(str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), option);
};

export const formatPrice = (amount) => {
  if (amount >= 1.0e9) {
    return `${(amount / 1.0e9).toFixed(1)}B`;
  }
  if (amount >= 1.0e6) {
    return `${(amount / 1.0e6).toFixed(1)}M`;
  }
  if (amount >= 1.0e4) {
    return `${(amount / 1.0e3).toFixed(1)}K`;
  }
  return amount;
};

export const formatDateFromObject = (dateObj) => {
  if (!dateObj) return '-';
  
  const dateString = dateObj.date_string || dateObj.dateString;
  const time = dateObj.time || '00:00';
  
  if (!dateString) return '-';
  return moment(`${dateString} ${time}`, 'YYYY-MM-DD HH:mm').format('DD MMM YYYY');
};

export const prettifyString = (str) =>
  isValidString(str) ? str[0].toUpperCase() + str.slice(1).toLowerCase() : '';

export const isNumberDecimal = (num) => num % 1 !== 0;

export const arrayToObject = (array, key) => {
  return array.reduce((obj, item) => {
    obj[item[key]] = item;
    return obj;
  }, {});
};

const numberSuperScripts = {
  0: 'th',
  1: 'st',
  2: 'nd',
  3: 'rd',
};

export const getSuperScriptTextForNumber = (num: number) => {
  return numberSuperScripts[num] || 'th';
};

export const getFileName = (data) => {
  const disposition = data && data.headers && data.headers['content-disposition'];
  let filename = '';
  if (disposition) {
    const matches = disposition.match(/filename="(.*)"/);
    filename = matches && matches.length && matches.length > 0 ? decodeURI(matches[1]) : '';
  }
  return filename;
};

export const getFormattedDate = (
  formatString: string,
  date: moment.MomentInput = new Date()
): string => {
  return moment(date).format(formatString);
};

export const rfqPlanTypes = [
  { label: 'ALL', value: 'All' },
  { label: 'EXPORT', value: 'Export' },
  { label: 'IMPORT', value: 'Import' },
  { label: 'LOCAL', value: 'Local' },
];

export const rfqTypesNames = {
  LOCAL: 'Local',
  CHARGES: 'Charges',
  EXPORT: 'Export',
  IMPORT: 'Import',
  TRADE: 'Trade',
};

export const formatNumberWithCommas = (num: string | number) => {
  return num?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

export const getIncoterms = ['EXW', 'FOB', 'FCA'];

export const importsSubTypeMap = {
  OCEAN_FREIGHT: 'SHIPPING_LINE',
  FREIGHT_MANAGEMENT: 'FF',
};

/**
 * Extract a subset of an object with only the required keys.
 * @param obj The source object
 * @param keys List of keys to be extracted (array of string)
 */
export const pickKeys = (obj, keys) => {
  return pickBy(obj, (val, key) => keys.includes(key)) as any;
};

export const isDemoOrganisation = () => {
  const organisationId = getOrgPrettyName();
  return DEMO_ORGANISATIONS.includes(organisationId);
};

export const numberFormatted = (
  val: string | number,
  {
    places,
    thousandSeparated,
  }: {
    places?: number;
    thousandSeparated?: boolean;
  }
): string => {
  if (!val) {
    console.error(`Not a valid number`);
    return val as any;
  }
  let num: number = typeof val === 'string' ? parseFloat(val as any) : (val as number);
  if (!num) {
    console.error(`Conversion of '${val}' to number failed!`);
    return val as any;
  }
  let str = num.toString();
  str = places ? num?.toFixed(places) : num.toString();
  str = thousandSeparated ? parseFloat(str).toLocaleString() : str;
  const totalDecimals = str?.split('.');
  if (totalDecimals.length === 2) {
      if (totalDecimals?.[1]?.split('').length < 2) {
        str = `${str}0`;
      }
  } else {
    str = `${str}.00`;
  }
  return str;
};

export const dateToNumerical = (date: any) => {
  return date ? moment(date)?.valueOf() : undefined
};

export const getNestedProperty = (parent: Object, keyPath: string[]) => {
  if (!parent) {
    return undefined;
  }
  return keyPath.reduce((acc, key) => acc?.[key], parent);
};

export const setNestedProperty = (parent: Object, keyPath: string[], value: any) => {
  if (!parent) {
    parent = {};
  }
  keyPath.reduce((acc, key, idx) => {
    if (!acc[key]) {
      acc[key] = {};
    }
    if (idx === keyPath.length - 1) {
      acc[key] = value;
    }
    return acc[key];
  }, parent);
};

export const isValidDate = (timeStamp): boolean =>  {
  return timeStamp && moment(timeStamp, true).isValid();
}

export const convertToPretty = (s: string, isUpperCase = false) => {
  if (!s) return '';
  const txt = s.replace('_', ' ').replace('-', ' ')
  return isUpperCase ? txt.toUpperCase() : txt.toLowerCase(); 
}

export const convertToPrettyString = (str: string) => {
  if (!isValidString(str)) return str;
  return str.toLowerCase()
  .replace(/[-\s]+/g, ' ')
  .replace(/(?:^|\s)\S/g, function(a) { 
      return a.toUpperCase(); 
  });
}
